import {
  CLINIC_MARKETPLACE_PAGE,
  COURT_FINDER_PAGE,
  TOURNAMENTS_PAGE,
  getFindPageUrl,
} from 'constants/pages';
import {
  EventCompetitivenessEnum,
  EventGroupFormatsEnum,
  EventTypesEnum,
  LessonTypesEnum,
  PlaySessionMatchCompetitivenessEnum,
} from 'types/generated/client';
import { FindTab } from 'screens/Find/types';
import { HomeIntentEnum } from 'screens/PlayerHomeV3/components/GetOnCourtSection/types';

// Intent classification — see DECISIONS-HOME-FEED.md §3a. Intents are derived
// client-side from what organizers already set; nothing in the schema stores them.
export interface ActivityIntents {
  primary: HomeIntentEnum;
  secondary: HomeIntentEnum[];
}

const ROUND_ROBIN_FORMATS = new Set<EventGroupFormatsEnum>([
  EventGroupFormatsEnum.RoundRobin,
  EventGroupFormatsEnum.RoundRobinSingleElimination,
  EventGroupFormatsEnum.VairRoundRobin,
]);

export const matchesIntent = (intents: ActivityIntents, intent: HomeIntentEnum) =>
  intents.primary === intent || intents.secondary.includes(intent);

export const classifyEvent = (event: {
  type: EventTypesEnum;
  groupFormat?: EventGroupFormatsEnum | null;
  groups: {
    competitiveness?: EventCompetitivenessEnum | null;
    isVairRatingSession?: boolean | null;
  }[];
}): ActivityIntents => {
  const groups = event.groups;
  const isVairRatingSession = groups.some((group) => group.isVairRatingSession);
  const isRoundRobin = !!event.groupFormat && ROUND_ROBIN_FORMATS.has(event.groupFormat);
  const isCasual =
    groups.length > 0 &&
    groups.every((group) => group.competitiveness === EventCompetitivenessEnum.Casual);

  if (isVairRatingSession) {
    return { primary: HomeIntentEnum.Compete, secondary: [HomeIntentEnum.Improve] };
  }
  if (isRoundRobin) {
    return {
      primary: HomeIntentEnum.Compete,
      secondary: isCasual ? [HomeIntentEnum.JoinGame] : [],
    };
  }
  if (event.type === EventTypesEnum.Tournament || event.type === EventTypesEnum.League) {
    return { primary: HomeIntentEnum.Compete, secondary: [] };
  }
  return { primary: HomeIntentEnum.JoinGame, secondary: [] };
};

/** Open plays carry no competitiveness and read as "Join a game". */
export const classifyGame = (game: {
  competitiveness?: PlaySessionMatchCompetitivenessEnum;
}): ActivityIntents =>
  game.competitiveness === PlaySessionMatchCompetitivenessEnum.Match
    ? { primary: HomeIntentEnum.Compete, secondary: [] }
    : { primary: HomeIntentEnum.JoinGame, secondary: [] };

export const classifyLesson = (lesson: { type: LessonTypesEnum }): ActivityIntents => {
  if (lesson.type === LessonTypesEnum.Cardio || lesson.type === LessonTypesEnum.Rfx) {
    return { primary: HomeIntentEnum.GetFit, secondary: [] };
  }
  return { primary: HomeIntentEnum.Improve, secondary: [] };
};

export const INTENT_LABEL: Record<HomeIntentEnum, string> = {
  [HomeIntentEnum.JoinGame]: 'Join a game',
  [HomeIntentEnum.Compete]: 'Compete',
  [HomeIntentEnum.Improve]: 'Improve',
  [HomeIntentEnum.GetFit]: 'Get fit',
  [HomeIntentEnum.BookCourt]: 'Book a court',
};

/** Where the empty state sends people to browse the full marketplace for an intent. */
export const INTENT_MARKETPLACE_URL: Record<HomeIntentEnum, string> = {
  // Pickup games live on /find, whose Games tab has no date cap.
  [HomeIntentEnum.JoinGame]: getFindPageUrl(FindTab.Games),
  [HomeIntentEnum.Compete]: TOURNAMENTS_PAGE,
  [HomeIntentEnum.Improve]: CLINIC_MARKETPLACE_PAGE,
  [HomeIntentEnum.GetFit]: CLINIC_MARKETPLACE_PAGE,
  [HomeIntentEnum.BookCourt]: COURT_FINDER_PAGE,
};
